import React, { useCallback, useEffect, useRef, useState } from "react";
import { TESTIMONIALS } from "./data";

const N = TESTIMONIALS.length;
const CLONES = 3;

// Infinite center-spotlight carousel, ported from the original vanilla JS slider:
// 3 clones on each side, transform in px, snap-back without transition on wrap.
export default function Testimonials() {
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const [trackPos, setTrackPos] = useState(CLONES);
  const [animate, setAnimate] = useState(false);
  const [modalIdx, setModalIdx] = useState(null);
  const transitioning = useRef(false);
  const autoTimer = useRef(null);

  const slides = [
    ...TESTIMONIALS.slice(N - CLONES).map((t, i) => ({ t, realIdx: N - CLONES + i, hidden: true })),
    ...TESTIMONIALS.map((t, i) => ({ t, realIdx: i, hidden: false })),
    ...TESTIMONIALS.slice(0, CLONES).map((t, i) => ({ t, realIdx: i, hidden: true })),
  ];

  const getVisible = () => (window.innerWidth > 768 ? 3 : 1);

  const applyTransform = useCallback((pos, withTransition) => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;
    const slideW = viewport.clientWidth / getVisible();
    track.style.transition = withTransition ? "transform 0.5s cubic-bezier(0.25,0.1,0.25,1)" : "none";
    track.style.transform = `translateX(${-pos * slideW}px)`;
  }, []);

  useEffect(() => {
    applyTransform(trackPos, animate);
  }, [trackPos, animate, applyTransform]);

  useEffect(() => {
    const onResize = () => applyTransform(trackPos, false);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [trackPos, applyTransform]);

  const next = useCallback(() => {
    if (transitioning.current) return;
    transitioning.current = true;
    setAnimate(true);
    setTrackPos((p) => p + 1);
    setTimeout(() => {
      setTrackPos((p) => {
        if (p >= CLONES + N) { setAnimate(false); return CLONES; }
        return p;
      });
      transitioning.current = false;
    }, 530);
  }, []);

  const prev = useCallback(() => {
    if (transitioning.current) return;
    transitioning.current = true;
    setAnimate(true);
    setTrackPos((p) => p - 1);
    setTimeout(() => {
      setTrackPos((p) => {
        if (p < CLONES) { setAnimate(false); return CLONES + N - 1; }
        return p;
      });
      transitioning.current = false;
    }, 530);
  }, []);

  const startAuto = useCallback(() => {
    autoTimer.current = setInterval(next, 3000);
  }, [next]);
  const clearAuto = useCallback(() => clearInterval(autoTimer.current), []);

  useEffect(() => {
    startAuto();
    return clearAuto;
  }, [startAuto, clearAuto]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setModalIdx(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const realIdx = (((trackPos - CLONES) % N) + N) % N;
  const centerIdx = getVisible() === 3 ? trackPos + 1 : trackPos;
  const modalT = modalIdx !== null ? TESTIMONIALS[modalIdx] : null;

  return (
    <>
      <section id="testimonials">
        <div className="section-inner">
          <span className="eyebrow reveal">Testimonials</span>
          <h2 className="section-title reveal">Trusted by <em>Artists &amp; Labels</em></h2>
        </div>
        <div className="testi-slider-wrap">
          <button className="testi-arrow testi-prev" aria-label="Previous testimonial"
            onClick={() => { clearAuto(); prev(); startAuto(); }}>❮</button>
          <div className="testi-viewport" ref={viewportRef}>
            <div className="testi-track" ref={trackRef}>
              {slides.map(({ t, realIdx: r, hidden }, i) => (
                <div key={i}
                  className={`testi-slide ${i === centerIdx ? "is-center" : "is-side"}`}
                  aria-hidden={hidden || undefined}>
                  <div className="testi-slide-inner" style={{ background: t.bg }}
                    onClick={() => setModalIdx(r)}>
                    <img src={t.img} alt={t.name} loading="lazy" />
                    <div className="testi-tap-hint">Tap to read</div>
                    <div className="testi-slide-overlay">
                      <div className="testi-slide-name">{t.name}</div>
                      <div className="testi-slide-label" style={{ color: t.color }}>{t.label}</div>
                      <div className={`testi-slide-type testi-slide-type-${t.type.toLowerCase()}`}>{t.type}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <button className="testi-arrow testi-next" aria-label="Next testimonial"
            onClick={() => { clearAuto(); next(); startAuto(); }}>❯</button>
          <div className="testi-dots">
            {TESTIMONIALS.map((_, i) => (
              <button key={i}
                className={`testi-dot${i === realIdx ? " active" : ""}`}
                aria-label={`Go to testimonial ${i + 1}`}
                onClick={() => { clearAuto(); setAnimate(true); setTrackPos(CLONES + i); startAuto(); }} />
            ))}
          </div>
        </div>
      </section>

      <div className={`testi-modal${modalT ? " open" : ""}`} aria-hidden={!modalT}>
        <div className="testi-modal-overlay" onClick={() => setModalIdx(null)} />
        {modalT && (
          <div className="testi-modal-box">
            <button className="testi-modal-close" aria-label="Close" onClick={() => setModalIdx(null)}>✕</button>
            <div className="testi-modal-photo-col">
              <img src={modalT.img} alt={modalT.name} />
            </div>
            <div className="testi-modal-body">
              <div className="testi-modal-name">{modalT.name}</div>
              <div className="testi-modal-role" style={{ color: modalT.color }}>{modalT.label}</div>
              <div className="testi-modal-years">{modalT.years} with FM Digital Official</div>
              <p className="testi-modal-text">{modalT.text}</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
